"use client";

import { useState } from "react";
import { ArrowLeft, Users, Brain, ListChecks } from "lucide-react";
import { GlowingEffect } from "@/components/ui/glowing-effect";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { leadershipRolesData } from "@/data/leadershipRolesData";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/context/LanguageContext";

type ViewMode = "competences" | "tasks";

export default function RolesPage() {
    const [viewMode, setViewMode] = useState<ViewMode>("competences");
    const { t } = useLanguage();

    return (
        <div className="min-h-screen">
            <SEO
                title={t("seo.leadership.roles.title")}
                description={t("seo.leadership.roles.description")}
                keywords={t("seo.leadership.roles.keywords").split(",").map(k => k.trim())}
            />
            <main id="main-content" className="container mx-auto px-4 py-12">
                <Link to="/tools/leadership_roles">
                    <Button variant="ghost" className="mb-8">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Leadership Roles
                    </Button>
                </Link>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-primary/10 rounded-xl">
                            <Users className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold">The 9 Leadership Roles</h1>
                            <p className="text-muted-foreground mt-1">Foundational archetypes for team self-organization</p>
                        </div>
                    </div>

                    <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)} className="w-[400px]">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="competences" className="flex items-center gap-2">
                                <Brain className="h-4 w-4" />
                                Skills & Competences
                            </TabsTrigger>
                            <TabsTrigger value="tasks" className="flex items-center gap-2">
                                <ListChecks className="h-4 w-4" />
                                Tasks
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>

                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {leadershipRolesData.map((role, index) => (
                        <GridItem
                            key={index}
                            title={role.title}
                            description={role.description}
                            categories={role.categories}
                            tasks={role.tasks}
                            viewMode={viewMode}
                        />
                    ))}
                </ul>
            </main>
            <Footer />
        </div>
    );
}

interface GridItemProps {
    title: string;
    description: string;
    categories: { name: string; items: string[] }[];
    tasks?: string[];
    viewMode: ViewMode;
}

const GridItem = ({ title, description, categories, tasks, viewMode }: GridItemProps) => {
    return (
        <li className="list-none min-h-[400px]">
            <div className="relative h-full rounded-[1.25rem] border-[0.75px] border-border p-2 md:rounded-[1.5rem] md:p-3">
                <GlowingEffect
                    spread={40}
                    glow={true}
                    disabled={false}
                    proximity={64}
                    inactiveZone={0.01}
                    borderWidth={3}
                />
                <div className="relative flex h-full flex-col overflow-hidden rounded-xl border-[0.75px] bg-background p-6 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)] gap-4">
                    <div className="space-y-2 pb-4 border-b">
                        <h3 className="text-xl font-bold font-sans tracking-tight text-foreground">{title}</h3>
                        <p className="text-sm text-muted-foreground">{description}</p>
                    </div>
                    <ScrollArea className="flex-1 pr-4 -mr-4">
                        <div className="space-y-6">
                            {viewMode === "competences" ? (
                                categories.map((cat, i) => (
                                    <div key={i} className="space-y-2">
                                        <h4 className="text-xs font-semibold uppercase tracking-wider text-primary">{cat.name}</h4>
                                        <ul className="list-disc pl-4 space-y-1">
                                            {cat.items.map((item, j) => (
                                                <li key={j} className="text-sm text-foreground/80 leading-snug">{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            ) : (
                                <ul className="list-disc pl-4 space-y-2">
                                    {tasks?.map((task, i) => (
                                        <li key={i} className="text-sm text-foreground/80 leading-snug">{task}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </div>
        </li>
    );
};
